import * as React from 'react';

import { shouldEnableConsumerRoleDiscoveryPages } from 'bundles/employability-common/feature-flags/learnerProfileEpicFeatureFlags';
import Skills from 'bundles/unified-career-academy/components/Skills';
import RecommendationsModule from 'bundles/unified-career-academy/components/modules/RecommendationsModule';
import AboutProfessionalCertsDescription from 'bundles/unified-career-academy/components/modules/about-certs/AboutProfessionalCertsDescription';
import AboutProfessionalCertsModule from 'bundles/unified-career-academy/components/modules/about-certs/AboutProfessionalCertsModule';
import CareerCardCredentials from 'bundles/unified-career-academy/components/modules/careers/CareerCardCredentials';
import CareerCardCredentialsNoLink from 'bundles/unified-career-academy/components/modules/careers/CareerCardCredentialsNoLink';
import CareerCardDescription from 'bundles/unified-career-academy/components/modules/careers/CareerCardDescription';
import CareerCardModule from 'bundles/unified-career-academy/components/modules/careers/CareerCardModule';
import CareerCardStats from 'bundles/unified-career-academy/components/modules/careers/CareerCardStats';
import CareersFooter from 'bundles/unified-career-academy/components/modules/careers/CareersFooter';
import CareersModule from 'bundles/unified-career-academy/components/modules/careers/CareersModule';
import FaqModule from 'bundles/unified-career-academy/components/modules/faq/FaqModule';
import ConsumerHeroCTA from 'bundles/unified-career-academy/components/modules/hero/ConsumerHeroCTA';
import ConsumerHeroModule from 'bundles/unified-career-academy/components/modules/hero/ConsumerHeroModule';
import TestimonialHeadshot from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialHeadshot';
import TestimonialVideo from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialVideo';
import TestimonialsModule from 'bundles/unified-career-academy/components/modules/testimonials/TestimonialsModule';
import TestimonialAuthor from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialAuthor';
import TestimonialQuoteModule from 'bundles/unified-career-academy/components/modules/testimonials/quote/TestimonialQuoteModule';
import ValuePropCard from 'bundles/unified-career-academy/components/modules/value-props/ValuePropCard';
import ValuePropsModule from 'bundles/unified-career-academy/components/modules/value-props/ValuePropsModule';
import { usePageContent } from 'bundles/unified-career-academy/contexts/PageContentContext';
import { useRecommendations } from 'bundles/unified-career-academy/contexts/RecommendationsContext';
import {
  filterConsumerCertificates,
  filterConsumerCertificatesNoDuplicatePartners,
} from 'bundles/unified-career-academy/utils/helpers';

import _t from 'i18n!nls/career-academy';

const ConsumerPageRootContent = () => {
  const pageContent = usePageContent();
  const recommendations = useRecommendations();
  const enableConsumerRoleDiscoveryPages = shouldEnableConsumerRoleDiscoveryPages();

  if (!pageContent) {
    return null;
  }

  return (
    <React.Fragment>
      <ConsumerHeroModule
        title={pageContent.heroTitle}
        subtitle={pageContent.heroSubtitle}
        description={pageContent.heroValueProps?.join(' | ')}
        cta={<ConsumerHeroCTA link={pageContent.heroCtaLink} text={pageContent.heroCtaText} />}
        backgroundImage={pageContent.heroBackgroundImage?.url}
      />
      <ValuePropsModule
        title={pageContent.valuePropTitle}
        subtitle={pageContent.valuePropSubtitle}
        partnerLogos={pageContent.valuePropPartnerLogosCollection?.items.map((item) => ({
          id: item?.sys.id,
          url: item?.image?.url,
          altText: item?.imageAltText || item?.title,
        }))}
        valuePropCards={pageContent.valuePropValuesCollection?.items.map((value) => (
          <ValuePropCard
            title={value?.title}
            subtitle={value?.subtitle}
            description={value?.description}
            iconName={value?.iconName}
          />
        ))}
      />
      <CareersModule
        title={pageContent.careersTitle}
        subtitle={pageContent.careersSubtitle}
        footer={<CareersFooter content={pageContent.careersFootnotes} />}
        careerCardsWithRoles={pageContent.careersCardsCollection?.items.map((card) => ({
          role: card?.jobCategory,
          card: (
            <CareerCardModule
              roleSlug={card?.slug}
              title={card?.title}
              url={enableConsumerRoleDiscoveryPages && card?.slug ? `/career-academy/roles/${card.slug}` : null}
              image={card?.image}
              description={<CareerCardDescription content={card?.description} />}
              skills={<Skills skills={card?.skills} />}
              careerStats={
                <CareerCardStats
                  // In order to use same contentful items across conusmer/enterprise. Replace asterisk with ¹ to comply with legal footnotes.
                  stats={card?.careerStats ? card.careerStats.replace(/\\\*/g, ' ¹') : card?.careerStats}
                />
              }
              credentials={
                enableConsumerRoleDiscoveryPages ? (
                  <CareerCardCredentialsNoLink
                    credentials={filterConsumerCertificatesNoDuplicatePartners(card?.programsCollection?.items)}
                  />
                ) : (
                  <CareerCardCredentials credentials={filterConsumerCertificates(card?.programsCollection?.items)} />
                )
              }
            />
          ),
        }))}
        showDomainFilter
      />
      <TestimonialsModule
        title={pageContent.testimonialsTitle}
        subtitle={pageContent.testimonialsSubtitle}
        headshots={pageContent.testimonialsUserTestimonialCollection?.items
          .filter((testimonial) => !testimonial?.videoEmbedUrl)
          .slice(0, 6)
          .map((testimonial, index) => (
            <TestimonialHeadshot
              name={testimonial?.authorName}
              imageUrl={testimonial?.image?.url}
              countryName={testimonial?.authorOrganization}
              index={index}
            />
          ))}
        testimonials={[
          // Create video testimonials if item has a video embed URL.
          ...(pageContent.testimonialsUserTestimonialCollection?.items
            .filter((testimonial) => !!testimonial?.videoEmbedUrl)
            .map((testimonial, index) => (
              <TestimonialVideo
                videoEmbedUrl={testimonial?.videoEmbedUrl}
                index={index}
                imageUrl={testimonial?.videoPlaceholderImage?.url || testimonial?.image?.url}
                authorName={testimonial?.authorName}
                authorTitle={testimonial?.authorTitle}
              />
            )) || []),
          // Create quote testimonials if item doesn't have a video embed URL.
          ...(pageContent.testimonialsUserTestimonialCollection?.items
            .filter((testimonial) => !testimonial?.videoEmbedUrl)
            .map((testimonial, index) => (
              <TestimonialQuoteModule
                index={index}
                text={testimonial?.text}
                author={
                  <TestimonialAuthor
                    authorName={testimonial?.authorName}
                    authorTitle={testimonial?.authorTitle}
                    imageUrl={testimonial?.image?.url}
                  />
                }
              />
            )) || []),
        ]}
      />
      <AboutProfessionalCertsModule
        title={pageContent.aboutTitle}
        subtitle={pageContent.aboutSubtitle}
        image={pageContent.aboutImage}
        description={<AboutProfessionalCertsDescription content={pageContent.aboutValueProps} />}
      />
      <RecommendationsModule
        collection={recommendations?.[0]}
        title={_t('Explore our career-changing Professional Certificates')}
        linkTitle={_t('Explore Certificates')}
        linkHref="/professional-certificates"
      />
      <FaqModule faqs={pageContent.faqsCollection?.items} title={_t('Frequently Asked Questions')} />
    </React.Fragment>
  );
};

export default ConsumerPageRootContent;
